import { LitElement, html } from "lit";

import { customElement, property } from "lit/decorators.js";

import { ResizeObserverController } from "ui-common/resize-observer-controller";

import "../confirmation-drawer";
import "../modal/wow-modal";

/**
 * A responsive confirmation component that automatically switches between
 * wow-confirmation-drawer (mobile) and wow-modal (desktop) based on viewport width.
 *
 * This component encapsulates the mobile detection logic and provides a consistent
 * API for confirmation dialogs across the application.
 *
 * @example
 * ```html
 * <wow-confirmation header="Are you sure you want to leave?">
 *   <div>
 *     <p>You have unsaved changes that will be lost.</p>
 *     <wow-button variant="neutral" size="medium" @click=${this.handleSave}>
 *       Save and Continue
 *     </wow-button>
 *     <wow-button variant="neutral" size="medium" @click=${this.handleContinue}>
 *       Continue Editing
 *     </wow-button>
 *   </div>
 * </wow-confirmation>
 * ```
 *
 * Mobile (< 768px): Renders as wow-confirmation-drawer (slides up from bottom)
 * Desktop (≥ 768px): Renders as wow-modal (centered overlay)
 *
 * @slot default - Content for the confirmation dialog
 */
@customElement("wow-confirmation")
export class WowConfirmation extends LitElement {
  /**
   * Header text for the confirmation dialog.
   * This text will be displayed in the header section of both modal and drawer variants.
   */
  @property({ type: String })
  header = "";

  private viewportController = new ResizeObserverController(this, () => document.body);

  /**
   * Determines if the current viewport should be considered mobile.
   * Uses the same breakpoint as wow-modal (768px) for consistency.
   */
  get isMobileView(): boolean {
    const width = this.viewportController.result?.width || window.innerWidth;
    return width < 768; // Same breakpoint as wow-modal
  }

  protected override render() {
    return this.isMobileView
      ? html`
          <wow-confirmation-drawer>
            <span slot="header">${this.header}</span>
            <slot></slot>
          </wow-confirmation-drawer>
        `
      : html`
          <wow-modal prevent-close variant="responsive">
            <span slot="header">${this.header}</span>
            <slot></slot>
          </wow-modal>
        `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "wow-confirmation": WowConfirmation;
  }
}
